import type { OpportunitySortField, OpportunityStatus, ProductType } from '@fig/shared';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isAdvisorStatusFilterValue } from '@/lib/advisor-status';
import {
  clearStoredAdvisorStatus,
  readStoredAdvisorStatus,
  writeStoredAdvisorStatus,
} from '@/lib/advisor-status-storage';
import { ALL_FILTER_VALUE } from '@/lib/filter-constants';
import { isIncomeStatusFilterValue } from '@/lib/income';
import { DEFAULT_OPPORTUNITY_STATUS, isOpportunityStatus } from '@/lib/opportunity-status';
import { isFigProductType } from '@/lib/product-types';

import type { OpportunityFilters } from './api/useOpportunities';

export type SortField = OpportunitySortField;
type SortDirection = 'asc' | 'desc';

interface FilterState {
  salesTeamId?: string;
  advisorId?: string;
  carrierName?: string;
  productType?: ProductType;
  outOfSurrender?: boolean;
  minScore?: number;
  maxScore?: number;
  minClientAge?: number;
  maxClientAge?: number;
  minSurrenderLoss?: number;
  maxSurrenderLoss?: number;
  taxQualification?: string;
  status?: OpportunityStatus;
  clientName?: string;
  incomeStatus?: string;
  advisorStatus?: string;
}

interface UseOpportunityFiltersReturn {
  page: number;
  pageSize: number;
  sortBy: SortField;
  sortOrder: SortDirection;
  filters: FilterState;
  /** Full filters object compatible with useOpportunities hook */
  queryFilters: OpportunityFilters;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSort: (field: SortField, direction?: SortDirection) => void;
  setFilter: (key: keyof FilterState, value: string | number | boolean | undefined) => void;
  clearFilters: () => void;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT_BY: SortField = 'propensity_score';
const DEFAULT_SORT_ORDER: SortDirection = 'desc';
const SORT_FIELDS = new Set<string>([
  'propensity_score',
  'estimated_value',
  'scored_at',
  'account_value',
  'surrender_loss',
  'carrier_name',
  'agent_name',
  'client_age',
  'surrender_date',
  'sales_team_name',
  'client_name',
  'policy_number',
  'income_benefit_value',
  'tax_qualification',
  'forwarded_at',
  'forwarded_by',
  'status',
  'has_notes',
]);

function isSortField(value: string | null): value is SortField {
  return !!value && SORT_FIELDS.has(value);
}

export function useOpportunityFilters(): UseOpportunityFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || DEFAULT_PAGE;
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;
  const sortParam = searchParams.get('sortBy');
  const sortBy = isSortField(sortParam) ? sortParam : DEFAULT_SORT_BY;
  const sortOrder = searchParams.get('sortOrder') === 'asc' ? 'asc' : DEFAULT_SORT_ORDER;

  const filters: FilterState = useMemo(() => {
    const f: FilterState = {};
    const salesTeamId = searchParams.get('salesTeamId');
    const advisorId = searchParams.get('advisorId');
    const carrierName = searchParams.get('carrierName');
    const productType = searchParams.get('productType');
    const outOfSurrender = searchParams.get('outOfSurrender');
    const minScore = searchParams.get('minScore');
    const maxScore = searchParams.get('maxScore');
    const minClientAge = searchParams.get('minClientAge');
    const maxClientAge = searchParams.get('maxClientAge');
    const minSurrenderLoss = searchParams.get('minSurrenderLoss');
    const maxSurrenderLoss = searchParams.get('maxSurrenderLoss');
    const taxQualification = searchParams.get('taxQualification');
    const status = searchParams.get('status');
    const clientName = searchParams.get('clientName');
    const incomeStatus = searchParams.get('incomeStatus');
    const advisorStatus = searchParams.get('advisorStatus');

    if (salesTeamId) f.salesTeamId = salesTeamId;
    if (advisorId) f.advisorId = advisorId;
    if (carrierName) f.carrierName = carrierName;
    if (isFigProductType(productType)) f.productType = productType;
    if (outOfSurrender === 'true') f.outOfSurrender = true;
    if (outOfSurrender === 'false') f.outOfSurrender = false;
    if (minScore) f.minScore = Number(minScore);
    if (maxScore) f.maxScore = Number(maxScore);
    if (minClientAge && Number.isFinite(Number(minClientAge)))
      f.minClientAge = Number(minClientAge);
    if (maxClientAge && Number.isFinite(Number(maxClientAge)))
      f.maxClientAge = Number(maxClientAge);
    if (minSurrenderLoss && Number.isFinite(Number(minSurrenderLoss)))
      f.minSurrenderLoss = Number(minSurrenderLoss);
    if (maxSurrenderLoss && Number.isFinite(Number(maxSurrenderLoss)))
      f.maxSurrenderLoss = Number(maxSurrenderLoss);
    if (taxQualification) f.taxQualification = taxQualification;
    if (clientName) f.clientName = clientName;
    if (isIncomeStatusFilterValue(incomeStatus)) f.incomeStatus = incomeStatus;
    if (isAdvisorStatusFilterValue(advisorStatus)) f.advisorStatus = advisorStatus;
    // Status defaults to 'new' unless an explicit status or the "all" sentinel
    // (show every status) is present in the URL.
    if (status === ALL_FILTER_VALUE) {
      f.status = undefined;
    } else {
      f.status = isOpportunityStatus(status) ? status : DEFAULT_OPPORTUNITY_STATUS;
    }

    return f;
  }, [searchParams]);

  const queryFilters: OpportunityFilters = useMemo(
    () => ({
      page,
      pageSize,
      sortBy,
      sortOrder,
      ...filters,
    }),
    [page, pageSize, sortBy, sortOrder, filters]
  );

  // T20-3323: the advisor-status filter is sticky. On a bare URL, restore the
  // stored selection and write it into the query string so page state stays
  // URL-addressable. A value already in the URL always wins -- otherwise a
  // shared link would silently render the recipient's own saved filter instead
  // of the view the sender meant to share.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;

    if (searchParams.has('advisorStatus')) return;

    const stored = readStoredAdvisorStatus();
    if (!stored) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('advisorStatus', stored);
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPage = useCallback(
    (p: number) => {
      updateParams({ page: p === DEFAULT_PAGE ? undefined : String(p) });
    },
    [updateParams]
  );

  const setPageSize = useCallback(
    (size: number) => {
      updateParams({
        pageSize: size === DEFAULT_PAGE_SIZE ? undefined : String(size),
        page: undefined, // reset to page 1
      });
    },
    [updateParams]
  );

  const setSort = useCallback(
    (field: SortField, direction?: SortDirection) => {
      const dir = direction ?? (sortBy === field && sortOrder === 'desc' ? 'asc' : 'desc');
      updateParams({
        sortBy: field === DEFAULT_SORT_BY ? undefined : field,
        sortOrder: dir === DEFAULT_SORT_ORDER ? undefined : dir,
        page: undefined,
      });
    },
    [updateParams, sortBy, sortOrder]
  );

  const setFilter = useCallback(
    (key: keyof FilterState, value: string | number | boolean | undefined) => {
      // Only the advisor-status filter persists (T20-3323); every other filter
      // still resets on a fresh visit.
      if (key === 'advisorStatus') {
        writeStoredAdvisorStatus(value === undefined ? undefined : String(value));
      }
      updateParams({
        [key]: value === undefined ? undefined : String(value),
        page: undefined, // reset to page 1 on filter change
      });
    },
    [updateParams]
  );

  const clearFilters = useCallback(() => {
    // Clearing is a manual adjustment, so the sticky value goes with it.
    // Without this the seed effect would put it straight back and Clear Filters
    // would appear to do nothing.
    clearStoredAdvisorStatus();
    seededRef.current = true;
    setSearchParams({});
  }, [setSearchParams]);

  return {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    queryFilters,
    setPage,
    setPageSize,
    setSort,
    setFilter,
    clearFilters,
  };
}
